import React, { useState, useEffect } from 'react';
import SensorGrid from '../components/SensorGrid';
import {fetchMachines, fetchSensorData} from '../services/sensorService';
import './SearchPage.css';

const SearchPage = () => {
  const [searchData, setSearchData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [machines, setMachines] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [selectedSensors, setSelectedSensors] = useState([]);
  const [isSensorOpen, setIsSensorOpen] = useState(false);
  const [filters, setFilters] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    filterType: 'all',
    machine: ''
  });

  useEffect(() => {
    const onClickOutside = (e) => {
      // 드롭다운 외부 클릭 시 닫기
      if (!e.target.closest('.sensor-group')) {
        setIsSensorOpen(false);
      }
    };
    document.addEventListener('click', onClickOutside);
    return () => document.removeEventListener('click', onClickOutside);
  }, []);
  
  const selectAllSensors = () => {
    setSelectedSensors(sensors.map(s => s.sensor_id));
  };
  const clearAllSensors = () => {
    setSelectedSensors([]);
  };
  
  useEffect(() => {
    const loadMachines = async () => {
      try {
        // const response = await fetch('http://localhost:8000/machines');
        const data = await fetchMachines();
        setMachines(data);
      } catch (error) {
        console.error('설비 목록 로드 오류:', error);
      }
    };
    loadMachines();

    // iframe 높이 자동 조정
    const adjustIframeHeight = () => {
      if (window.parent !== window) {
        // 실제 콘텐츠 높이 측정 (스크롤바 고려)
        const bodyHeight = document.body.scrollHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const offsetHeight = document.body.offsetHeight;
        
        // 가장 정확한 높이 사용
        const height = Math.max(bodyHeight, documentHeight, offsetHeight);
        
        console.log('높이 측정:', { bodyHeight, documentHeight, offsetHeight, final: height });
        window.parent.postMessage({ type: 'resize', height }, '*');
      }
    };

    // 페이지 로드 후 높이 조정 (여러 번 시도)
    setTimeout(adjustIframeHeight, 100);
    setTimeout(adjustIframeHeight, 500);
    setTimeout(adjustIframeHeight, 1000);
    
    // 데이터 로드 후에도 높이 조정
    const timer = setTimeout(adjustIframeHeight, 2000);
    
    return () => clearTimeout(timer);
  }, [searchData]);

  // 설비가 변경될 때 센서 목록 가져오기
  useEffect(() => {
    const loadSensors = async () => {
      if (!filters.machine) {
        setSensors([]);
        setSelectedSensors([]);
        return;
      }
      
      try {
        const response = await fetch(`http://localhost:8000/sensors?machine=${encodeURIComponent(filters.machine)}`);
        const data = await response.json();
        setSensors(data);
        // 모든 센서를 기본 선택
        setSelectedSensors(data.map(sensor => sensor.sensor_id));
      } catch (error) {
        console.error('센서 목록 로드 오류:', error);
        setSensors([]);
      }
    };
    
    loadSensors();
  }, [filters.machine]);

  const handleSensorChange = (sensorId, checked) => {
    if (checked) {
      setSelectedSensors(prev => [...prev, sensorId]);
    } else {
      setSelectedSensors(prev => prev.filter(id => id !== sensorId));
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.filterType !== 'all') params.append('filter_type', filters.filterType);
      if (filters.machine) params.append('machine', filters.machine);
      //if (selectedSensors.length > 0) params.append('sensors', selectedSensors.join(','));

      const data = await fetchSensorData(filters.startDate, filters.endDate, filters.filterType, filters.machine);
      console.log('SearchPage 데이터:', data.slice(0, 3)); // 처음 3개 데이터 확인
      setSearchData(data);
      
      // 검색 후 높이 재조정
      setTimeout(() => {
        if (window.parent !== window) {
          const bodyHeight = document.body.scrollHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const offsetHeight = document.body.offsetHeight;
          const height = Math.max(bodyHeight, documentHeight, offsetHeight);
          
          console.log('검색 후 높이 측정:', { bodyHeight, documentHeight, offsetHeight, final: height });
          window.parent.postMessage({ type: 'resize', height }, '*');
        }
      }, 200);
    } catch (error) {
      console.error('검색 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCsv = () => {
    if (searchData.length === 0) {
      alert('내보낼 데이터가 없습니다.');
      return;
    }

    const headers = ['ID', '토픽', '머신', '온도', '최소값', '최대값', '상태', '생성일시'];
    const csvContent = [
      headers.join(','),
      ...searchData.map((row, index) => [
        index + 1,
        row.topic,
        row.machine,
        row.temp,
        row.min,
        row.max,
        row.ispass ? 'PASS' : 'FAIL',
        row.created_at
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `sensor_data_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    // <div className="search-page" style={{ height: '100vh', overflow: 'hidden' }}>
    <div className="search-page">
      <div className="search-controls">
        <div className="control-group">
          <label>시작일:</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({...filters, startDate: e.target.value})}
          />
        </div>
        <div className="control-group">
          <label>종료일:</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({...filters, endDate: e.target.value})}
          />
        </div>
        <div className="control-group">
          <label>설비:</label>
          <select
            value={filters.machine}
            onChange={(e) => setFilters({...filters, machine: e.target.value})}
          >
            <option value="">전체</option>
            {machines.map(machine => (
              <option key={machine.machine_id} value={machine.machine_name}>
                {machine.machine_name}
              </option>
            ))}
          </select>
        </div>
        <div className="control-group sensor-group hidden">
          <label>센서(온도계):</label>

          {/* 트리거(닫혀 있을 때 보이는 한 줄짜리 입력 모양) */}
          <div
            className="sensor-trigger"
            tabIndex={0}
            onClick={() => setIsSensorOpen(prev => !prev)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsSensorOpen(prev => !prev);
              }
            }}
            aria-haspopup="listbox"
            aria-expanded={isSensorOpen}
          >
            <span style={{ color: selectedSensors.length ? '#333' : '#999' }}>
              {sensors.length === 0
                ? '설비를 선택하면 센서 목록이 표시됩니다.'
                : selectedSensors.length === 0
                  ? '센서 선택'
                  : selectedSensors.length === sensors.length
                    ? `전체 선택 (${sensors.length})`
                    : `${selectedSensors.length}개 선택`}
            </span>
            <span aria-hidden>▾</span>
          </div>

          {/* 드롭다운 메뉴(펼쳐진 상태에서만 표시) */}
          {isSensorOpen && sensors.length > 0 && (
            <div className="sensor-menu" role="listbox" aria-multiselectable="true">
              <div className="sensor-menu-toolbar">
                <button type="button" onClick={selectAllSensors}>전체 선택</button>
                <button type="button" onClick={clearAllSensors}>전체 해제</button>
              </div>

              {sensors.map(sensor => (
                <label key={sensor.sensor_id} className="sensor-option">
                  <input
                    type="checkbox"
                    checked={selectedSensors.includes(sensor.sensor_id)}
                    onChange={(e) => handleSensorChange(sensor.sensor_id, e.target.checked)}
                  />
                  <span>{sensor.sensor_name}</span>
                </label>
              ))}
            </div>
          )}

          {/* 센서가 없을 때 안내(트리거 텍스트로도 안내하지만 명시적으로 보여주고 싶다면 유지) */}
          {isSensorOpen && sensors.length === 0 && (
            <div className="sensor-menu">
              <div className="no-sensors">설비를 선택하면 센서 목록이 표시됩니다.</div>
            </div>
          )}
        </div>
        <div className="control-group">
          <label>상태:</label>
          <select
            value={filters.filterType}
            onChange={(e) => setFilters({...filters, filterType: e.target.value})}
          >
            <option value="all">전체</option>
            <option value="pass">정상</option>
            <option value="fail">이상</option>
          </select>
        </div>
        <div className="button-groups">
          <button onClick={handleSearch} disabled={loading}>
            {loading ? '검색중...' : '검색'}
          </button>
          <button onClick={exportToCsv} disabled={searchData.length === 0}>
            CSV 내보내기
          </button>
        </div>
      </div>

      <div className="search-container">
        {loading && <div className="loading">데이터를 불러오는 중...</div>}
        {!loading && searchData.length === 0 && (
          <div className="no-data">검색된 데이터가 없습니다.</div>
        )}
        {!loading && searchData.length > 0 && (
          <div>
            <h3 style={{margin: '0 20px 15px 20px'}}>검색 결과 ({searchData.length}건)</h3>
            <div className="tableScrollWrapper">
              <SensorGrid data={searchData} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;